import { Menu, Transition } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { classNames, darkenColor } from '../../../utils/helpers';
import { Dispatch, Fragment, SetStateAction } from 'react';
import Drivers from './Calendar/Drivers';
import { useSelector } from 'react-redux';
import Note from './Note';
import Todo from '../../Todo';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import {
  setDeleteBooking,
  setBookingDate,
  setBooking,
} from './../../../store/actionCreators';
import {
  ArrowPathIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { RootReducerState } from '../../../store/reducer';

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

interface HeaderProps {
  date: Date;
  scheduleLimit: number;
  setSchedule: (value: any) => void;
  setDate: Dispatch<SetStateAction<Date>>;
  fetchSchedule: (day?: string) => Promise<void>;
  showNote: boolean;
  setShowNote: (val: boolean) => void;
  fetchBookings: () => Promise<void>;
  handleOpenDatePicker: (value: any) => void;
  setOpenDatePicker: Dispatch<SetStateAction<boolean>>;
  openDatePicker: boolean;
}

const Header = ({
  date,
  scheduleLimit,
  setSchedule,
  setDate,
  fetchSchedule,
  setShowNote,
  showNote,
  fetchBookings,
  handleOpenDatePicker,
  setOpenDatePicker,
  openDatePicker,
}: HeaderProps) => {
  const branch = useSelector(
    (state: RootReducerState) => state.dashboard.selectedBranch
  );
  const router = useRouter();
  const dispatch = useDispatch();
  const refresh = () => {
    const promise = fetchBookings();

    toast.promise(promise, {
      loading: 'Refreshing...',
      success: 'Refreshed!',
      error: 'Error when refreshing',
    });
  };

  const redirectCreate = () => {
    dispatch(setDeleteBooking());
    dispatch(setBookingDate(date));
    dispatch(setBooking('saleType', { label: 'Retail Cash', value: '2' }));
    router.push('bookings/create');
  };

  const handleChangeDate = (event) => {
    setDate(event);
    toast.promise(fetchSchedule(event.toISOString().split('T')[0]), {
      loading: 'Saving...',
      success: 'Saved!',
      error: 'Error when saving',
    });
    setOpenDatePicker((prev) => !prev);
  };

  return (
    <header className="md:pr-68 fixed top-16 z-30 flex w-full flex-none items-center justify-between border-b border-gray-200 bg-white py-4 px-4 shadow-sm sm:px-72 md:pl-8">
      <div>
        <h1 className="text-lg font-semibold leading-6 text-gray-900">
          {/* <time dateTime="2022-01-22" className="sm:hidden">
            Jan 22, 2022
          </time> */}
          <time dateTime="2022-01-22" className="hidden sm:inline">
            {date.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </time>
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {date.toLocaleDateString('en-US', {
            weekday: 'long',
          })}
        </p>
      </div>
      <div className="flex items-center">
        <Drivers
          branchColour={branch.branch_colour}
          scheduleLimit={scheduleLimit}
          updateSchedule={setSchedule}
          max={branch.vehicles?.length}
        />
        <div className="mx-6 h-6 w-px bg-gray-300" />

        <div className="relative flex items-center rounded-md shadow-sm md:items-stretch">
          <button
            type="button"
            onClick={() => {
              setDate(addDays(date, -1));
              toast.promise(fetchSchedule(addDays(date, -1).toISOString()), {
                loading: 'Saving...',
                success: 'Saved!',
                error: 'Error when saving',
              });
            }}
            className="flex items-center justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleOpenDatePicker}
            className="hidden border-t border-b border-gray-300 bg-white px-3.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:relative md:block"
          >
            Calendar
          </button>

          <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
          <button
            type="button"
            onClick={() => {
              setDate(addDays(date, 1));
              toast.promise(fetchSchedule(addDays(date, 1).toISOString()), {
                loading: 'Saving...',
                success: 'Saved!',
                error: 'Error when saving',
              });
            }}
            className="flex items-center justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          {openDatePicker && (
            <div className="absolute top-10">
              <DatePicker selected={date} onChange={handleChangeDate} inline />
            </div>
          )}
        </div>

        <div className="mx-6 h-6 w-px bg-gray-300" />

        <button
          onClick={refresh}
          type="button"
          className="focus:ring-sortme-pink ml-auto rounded-md border border-gray-300 bg-white p-1.5 text-gray-600 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
          <span className="sr-only">Refresh</span>
          <ArrowPathIcon
            className={classNames(
              'h-5 w-5'
              // isRefreshing && 'animate-reverse-spin'
            )}
            aria-hidden="true"
          />
        </button>

        <div className="mx-6 h-6 w-px bg-gray-300" />

        <div className="relative">
          <Note date={date} showNote={showNote} setShowNote={setShowNote} />
        </div>

        <div className="mx-6 h-6 w-px bg-gray-300" />

        <div className="relative">
          <Todo onTodoOpen={() => setShowNote(false)} date={date} />
        </div>

        <div className="hidden md:flex md:items-center">
          <div className="ml-6 h-6 w-px bg-gray-300" />

          <button
            onClick={() => redirectCreate()}
            type="button"
            style={{
              backgroundColor: branch.branch_colour,
              marginLeft: '1.5rem',
              borderRadius: '0.375rem',
              border: '1px solid transparent',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: 'white',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              outline: 'none',
              transition: 'background-color 0.3s, box-shadow 0.3s',
            }}
            onMouseEnter={(e) =>
              ((e.target as HTMLDivElement).style.backgroundColor = darkenColor(
                branch.branch_colour,
                20
              ))
            }
            onMouseLeave={(e) =>
              ((e.target as HTMLDivElement).style.backgroundColor =
                branch.branch_colour)
            }
            onFocus={(e) =>
              (e.target.style.boxShadow = `0 0 0 2px ${branch.branch_colour}`)
            }
            onBlur={(e) =>
              (e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)')
            }
          >
            Create booking
          </button>
        </div>
        <Menu as="div" className="relative ml-6 md:hidden">
          <Menu.Button className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
            <span className="sr-only">Open menu</span>
            <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="#"
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'block px-4 py-2 text-sm'
                      )}
                    >
                      Create booking
                    </a>
                  )}
                </Menu.Item>
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <a
                      href="#"
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'block px-4 py-2 text-sm'
                      )}
                    >
                      Go to today
                    </a>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
};

export default Header;
